<template>
  <v-form
    ref="form"
    v-model="isFormValid"
  >
    <v-row class="mt-0 d-flex align-baseline">
      <v-col
        cols="12"
        md="4"
        class="primary--text font-weight-bold text-h6"
      >
        {{ $t("commons.name") }}
      </v-col>
      <v-col class="pb-0 mb-0">
        <template v-if="readOnly">{{ formValues.name || "-" }} </template>
        <v-text-field
          v-else
          clearable
          @input="handleInputOther"
          :rules="rules.name"
          type="text"
          :label="$t('commons.name')"
          v-model="formValues.name"
          dense
          outlined
        />
      </v-col>
    </v-row>
    <v-row class="mt-0 d-flex align-baseline">
      <v-col
        cols="12"
        md="4"
        class="primary--text font-weight-bold text-h6"
      >
        {{ $t("commons.date.start_date") }}
      </v-col>
      <v-col class="pb-0 mb-0">
        <template v-if="readOnly">{{ formatDatetime(formValues.start_date) }} </template>
        <DatetimePicker
          v-else
          @input="handleInputOther"
          v-model="formValues.start_date"
          :textFieldProps="{
            placeholder: $t('commons.date.start_date'),
            rules: rules.start_date
          }"
        />
      </v-col>
    </v-row>
    <v-row class="mt-0 d-flex align-baseline">
      <v-col
        cols="12"
        md="4"
        class="primary--text font-weight-bold text-h6"
      >
        {{ $t("commons.date.end_date") }}
      </v-col>
      <v-col class="pb-0 mb-0">
        <template v-if="readOnly">{{ formatDatetime(formValues.end_date) }} </template>
        <DatetimePicker
          v-else
          @input="handleInputOther"
          v-model="formValues.end_date"
          :min="formValues.start_date"
          :textFieldProps="{
            placeholder: $t('commons.date.end_date'),
            rules: rules.end_date
          }"
        />
      </v-col>
    </v-row>
    <v-row class="mt-0 d-flex align-baseline">
      <v-col
        cols="12"
        md="4"
        class="primary--text font-weight-bold text-h6"
      >
        {{ $t("commons.status.name") }}
      </v-col>
      <v-col class="pb-0 mb-0">
        <template v-if="readOnly">{{ formValues.status || "-" }} </template>
        <StatusInput
          v-else
          v-model="formValues.status"
          :rules="rules.status"
          @input="handleInputOther"
        />
      </v-col>
    </v-row>

    <v-row class="mt-0 d-flex align-baseline">
      <v-col
        cols="12"
        md="4"
        class="primary--text font-weight-bold text-h6"
      >
        {{ $t("commons.attachments") }}
      </v-col>
      <v-col class="pb-0 mb-0">
        <template v-if="readOnly">
          <v-row class="ml-1">
            <div
              v-for="(attachment, index) in formValues.attachments"
              :key="index"
              class="file-icon"
            >
              <a
                :href="attachment.file_url"
                target="_blank"
                ><v-icon medium>mdi-file</v-icon></a
              >
            </div>
          </v-row>
        </template>
        <template v-else>
          <v-row class="d-flex align-center ml-1">
            <!-- Bouton d'upload -->
            <v-btn
              outlined
              color="primary"
              dense
              :disabled="readOnly"
              @click="triggerFileUpload"
              class="mr-5"
            >
              <v-icon left>mdi-upload</v-icon>
              {{ $t("commons.attachments") }}
            </v-btn>

            <!-- Affichage des fichiers chargés -->
            <div
              v-for="(attachment, index) in formValues.attachments"
              :key="index"
              class="d-flex align-center mr-4"
            >
              <!-- Si le fichier a un lien existant -->
              <template v-if="attachment.file_url">
                <a
                  :href="attachment.file_url"
                  target="_blank"
                  class="d-flex align-center"
                >
                  <v-icon
                    medium
                    class="mr-2"
                    >mdi-file</v-icon
                  >
                  <!-- File {{ index + 1 }} -->
                  {{ attachment.name }}
                </a>
              </template>
              <!-- Si c'est un nouveau fichier -->
              <template v-else>
                <div class="d-flex align-center">
                  <v-icon
                    medium
                    class="mr-2"
                    >mdi-file</v-icon
                  >
                  <!-- File {{ index + 1 }} -->
                  {{ attachment.name }}
                </div>
              </template>
              <v-btn
                icon
                small
                color="red"
                @click="removeFile(index)"
                class="ml-2"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </div>

            <!-- Input invisible pour le téléchargement -->
            <input
              ref="fileInput"
              type="file"
              multiple
              :disabled="readOnly"
              @change="handleFilesChange"
              style="display: none"
            />
          </v-row>
        </template>
      </v-col>
    </v-row>
    <v-row
      class="mt-0 d-flex align-baseline"
      v-if="!readOnly"
    >
      <v-col
        cols="12"
        md="4"
        class="primary--text font-weight-bold text-h6"
      >
        {{ $tc("categories.notification.name", 1) }}
      </v-col>
      <v-col class="pb-0 mb-0">
        <v-checkbox
          :disabled="readOnly"
          v-model="formValues.notify_oncreated"
          @change="handleNotifyOnCreatedChange"
        >
          <template v-slot:label>
            <div>{{ $t("categories.inspection.send_notifications") }}</div>
          </template>
        </v-checkbox>
      </v-col>
      <v-col class="pb-0 mb-0">
        <v-checkbox
          :disabled="readOnly"
          v-model="formValues.notify_team_oncreated"
          @change="handleNotifyTeamOnCreatedChange"
        >
          <template v-slot:label>
            <div>{{ $t("categories.inspection.send_select_team_notifications") }}</div>
          </template>
        </v-checkbox>
      </v-col>
    </v-row>
    <v-row
      class="mt-0 d-flex align-baseline"
      v-else
    >
      <v-col
        cols="12"
        md="4"
        class="primary--text font-weight-bold text-h6"
      >
        {{ $tc("categories.notification.name", 1) }}
      </v-col>
      <v-col
        class="pb-0 mb-0"
        :cols="readOnly ? 6 : 6"
      >
        <v-checkbox
          :disabled="readOnly"
          v-model="formValues.notify_oncreated"
          @change="handleNotifyOnCreatedChange"
        >
          <template v-slot:label>
            <div>{{ $t("categories.inspection.send_notifications") }}</div>
          </template>
        </v-checkbox>
        <v-checkbox
          :disabled="readOnly"
          v-model="formValues.notify_team_oncreated"
          @change="handleNotifyTeamOnCreatedChange"
        >
          <template v-slot:label>
            <div>{{ $t("categories.inspection.send_select_team_notifications") }}</div>
          </template>
        </v-checkbox>
      </v-col>
    </v-row>
    <v-row class="mt-0 d-flex align-baseline">
      <v-col
        cols="12"
        md="4"
        class="primary--text font-weight-bold text-h6"
      ></v-col>
      <v-col
        v-if="formValues.notify_team_oncreated && !readOnly"
        cols="12"
        md="8"
      >
        <v-select
          :disabled="readOnly"
          v-model="formValues.users"
          :items="users"
          item-text="email"
          item-value="id"
          :label="$t('commons.send_to')"
          multiple
          chips
          attach
          outlined
          @input="handleInputOther"
        ></v-select>
      </v-col>
    </v-row>
    <v-row class="mt-0 d-flex align-baseline">
      <v-col
        cols="12"
        md="4"
        class="primary--text font-weight-bold text-h6"
      >
        {{ $t("commons.send_to_externes") }}
      </v-col>
      <v-col class="pb-0 mb-0">
        <template v-if="readOnly">
          <v-col
            class="pl-0 pb-0 mb-0"
            cols="12"
            md="7"
          >
            <v-combobox
              chips
              multiple
              v-model="formValues.external_emails"
              :placeholder="$t('commons.send_to_externes')"
              :disabled="readOnly"
              clearable
              dense
              outlined
              :label="$t('commons.send_to_externes')"
            >
              <template #item="{ item }">
                <v-list-item-content>{{ item }}</v-list-item-content>
                <avs-deleteButton
                  :shape="{ text: true, small: true }"
                  :spantext="$t('categories.nonconformity.remove_list')"
                />
              </template>
            </v-combobox>
          </v-col>
        </template>
        <template v-else>
          <v-combobox
            chips
            multiple
            v-model="formValues.external_emails"
            :placeholder="$t('commons.send_to_externes')"
            clearable
            outlined
            :label="$t('commons.send_to_externes')"
            @input="handleInputOther"
          >
            <template #item="{ item }">
              <v-list-item-content>{{ item }}</v-list-item-content>
              <avs-deleteButton
                :shape="{ text: true, small: true }"
                :spantext="$t('categories.nonconformity.remove_list')"
              />
            </template>
          </v-combobox>
        </template>
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import _ from "lodash"
import { TeamService } from "@/services"
import { mkInspection } from "@/utils/datamodels"
import { toDatetime } from "@/utils/datetime"
import { required, dateCompare } from "@/utils/validator"

export default {
  name: "InspectionForm",
  components: {
    DatetimePicker: () => import("@/components/inputs/DatetimePicker.vue"),
    StatusInput: () => import("@/components/inputs/StatusInput.vue")
  },
  props: {
    value: {
      type: Object,
      required: true
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    validateCounter: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      isFormValid: true,
      attachments: [],
      selectedFilesCount: 0,
      formValues: mkInspection(),
      users: []
    }
  },
  computed: {
    rules() {
      return {
        name: [required],
        status: [required],
        start_date: [required],
        attachments: [],
        end_date: [
          required,
          () => dateCompare(this.formValues.start_date, this.formValues.end_date)
        ]
      }
    },
    projectId() {
      return this.$store.state.app.project.id
    }
  },
  created() {
    this.fetchUsers()
  },
  methods: {
    formatDatetime(value) {
      return value ? toDatetime(value) : "-"
    },
    removeFile(index) {
      // Supprime le fichier sélectionné de la liste des attachments
      this.formValues.attachments.splice(index, 1)
      this.selectedFilesCount = this.formValues.attachments.length
      // Émet un événement pour mettre à jour les valeurs du formulaire
      this.handleInputOther()
    },
    handleInputOther() {
      this.$emit("input", this.formValues)
    },
    handleInput() {
      this.$emit("input", this.formValues)
    },
    handleNotifyOnCreatedChange() {
      if (this.formValues.notify_oncreated) {
        this.formValues.notify_team_oncreated = false
        this.formValues.users = []
      }
      this.handleInputOther()
    },
    handleNotifyTeamOnCreatedChange() {
      if (this.formValues.notify_team_oncreated) {
        this.formValues.notify_oncreated = false
      } else {
        this.formValues.users = []
      }
      this.handleInputOther()
    },
    fetchUsers() {
      TeamService.getTeam(this, this.projectId).then(response => {
        this.users = response
      })
    },
    maxTotalSize(maxSize) {
      return value => {
        if (Array.isArray(value)) {
          const totalSize = value.reduce((sum, file) => sum + file.size, 0)
          if (totalSize > maxSize) {
            return "Total file size should not exceed 25MB."
          }
        }
        return true
      }
    },
    triggerFileUpload() {
      this.$refs.fileInput.click()
    },
    handleFilesChange(e) {
      // Récupère les nouveaux fichiers ajoutés via l'input file
      const files = e.target.files || e.files

      // Boucle sur chaque nouveau fichier et le transforme en objet Blob
      Array.from(files).forEach(file => {
        const blob_obj = new Blob([file], { type: file.type })

        // Utilise FileReader pour lire le contenu du Blob
        const reader = new FileReader()
        reader.onload = () => {
          const blobObject = {
            file: reader.result, // Contenu du fichier lu (base64 ou texte)
            name: file.name, // Nom du fichier
            type: file.type, // Type MIME
            blob: blob_obj // Le Blob pour une utilisation directe
          }

          // Ajoute le nouvel objet Blob à la liste existante
          this.formValues.attachments.push(blobObject)

          // Émet un événement pour mettre à jour les valeurs du formulaire
          this.handleInputOther()
        }

        // Lire le fichier en base64
        reader.readAsDataURL(blob_obj)
      })

      // Met à jour le compteur des fichiers sélectionnés
      this.selectedFilesCount = this.formValues.attachments.length
    }
    /*handleFilesChange(e) {
      // Récupère les fichiers depuis l'input file
      const files = e.target.files || e.files
      if (this.selectedFilesCount == 0) {
        this.formValues.attachments = []
      }

      // Boucle sur chaque fichier
      Array.from(files).forEach(file => {
        const blob_obj = new Blob([file], { type: file.type })

        // Utilise FileReader pour lire le contenu du Blob
        const reader = new FileReader()
        reader.onload = () => {
          const blobObject = {
            file: reader.result, // Contenu du fichier lu (base64 ou texte)
            name: file.name, // Nom du fichier
            type: file.type,
            blob: blob_obj // Garde aussi le Blob pour une utilisation directe
          }

          // Ajoute l'objet Blob et son contenu aux attachements
          this.formValues.attachments.push(blobObject)

          // Émet un événement pour mettre à jour les valeurs du formulaire
          this.handleInput()
        }

        // Choisissez une méthode de lecture en fonction de vos besoins :
        reader.readAsDataURL(blob_obj)
        this.selectedFilesCount = files.length // Lit le contenu en base64 (pour les images, etc.)
      })
    }*/
  },
  mounted() {
    if (this.value) this.formValues = _.cloneDeep(this.value)
  },
  watch: {
    validateCounter(newValue) {
      this.$refs["form"].validate()
    },
    isFormValid(newValue) {
      this.$emit("validated", newValue)
    },
    value(newValue, oldValue) {
      if (newValue && newValue !== oldValue) {
        this.formValues = _.cloneDeep(this.value)
      }
    }
  }
}
</script>
